import { useEffect, useState } from 'react';
import {
  getAuth,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
} from 'firebase/auth';
import { firebaseAuth } from '../../config/firebase';

const auth = getAuth();
// Indicates that the state will be persisted even when the browser window is closed
// or the activity is destroyed in React Native. An explicit sign out is needed to
// clear that state.
setPersistence(auth, browserLocalPersistence);

export function useAuth() {
  const [user, setUser] = useState();

  useEffect(() => {
    const unsubscribeFromAuthStatusChanged = onAuthStateChanged(
      auth,
      (user) => {
        if (user) {
          // User is signed in, see docs for a list of available properties
          // https://firebase.google.com/docs/reference/js/firebase.User
          setUser(user);
        } else {
          setUser(null);
        }
      }
    );

    return unsubscribeFromAuthStatusChanged;
  }, []);

  const reloadUser = async () => {
    await user?.reload();
    setUser({ ...user });
  };

  const validateEmail = (email) => {
    const regex = /^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/;
    return regex.test(email);
  };

  const _createUserWithEmailAndPassword = async (email, password) => {
    const userCredential = await createUserWithEmailAndPassword(
      firebaseAuth,
      email,
      password
    );
    await sendEmailVerification(userCredential.user);
    return userCredential;
  };

  const _sendEmailVerification = async () => {
    await sendEmailVerification(user);
  };

  const _sendPasswordResetEmail = async (email) => {
    await sendPasswordResetEmail(firebaseAuth, email);
  };

  const _signInWithEmailAndPassword = async (email, password) => {
    return await signInWithEmailAndPassword(firebaseAuth, email, password);
  };

  const signOut = async () => {
    await firebaseAuth.signOut();
  };

  return {
    user,
    createUserWithEmailAndPassword: _createUserWithEmailAndPassword,
    reloadUser,
    validateEmail,
    sendEmailVerification: _sendEmailVerification,
    sendPasswordResetEmail: _sendPasswordResetEmail,
    signInWithEmailAndPassword: _signInWithEmailAndPassword,
    signOut,
  };
}
